import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";

import {
  calculateEdgeDate,
  calculateRangeDaysWidth,
  calculateThumbLeft,
  calculateThumbWidth,
  Direction,
  MIN_THUMB_WIDTH,
  Range,
  THUMB_RANGE_DAY_WIDTH_INITIAL_VALUE,
  ThumbRangeDaysWidth
} from "./helpers";

const useChangeRange = (
  thumbWidth: MutableRefObject<number>,
  thumbLeft: MutableRefObject<number>,
  calendarElement: HTMLElement,
  range: Range,
  onRangeChange: (Range) => void,
  calendarStartDate: string,
  calendarEndDate: string
) => {
  const [draggedEdge, setDraggedEdge] = useState<Direction | null>(null);
  const [lastEdgePosition, setLastEdgePosition] = useState<number>(0);

  const [startDate, setStartDate] = useState(range.start);
  const [endDate, setEndDate] = useState(range.end);

  const prevThumbWidth = useRef<number>(0);
  const prevThumbLeft = useRef<number>(0);
  const rangeDaysWidth = useRef<ThumbRangeDaysWidth>(THUMB_RANGE_DAY_WIDTH_INITIAL_VALUE);

  const { formatDatabaseDateTime } = useDateFormatter();

  const handleThumbEdgeMouseDown = useCallback(
    (e, kind: Direction) => {
      if (!calendarElement) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setLastEdgePosition(e.clientX);
      prevThumbWidth.current = thumbWidth.current;
      prevThumbLeft.current = thumbLeft.current;
      rangeDaysWidth.current = calculateRangeDaysWidth(
        thumbWidth.current,
        thumbLeft.current,
        range,
        calendarStartDate,
        calendarEndDate,
        calendarElement.offsetWidth
      );
      setDraggedEdge(kind);
    },
    [calendarElement, range]
  );

  const handleDocumentMouseUp = useCallback(
    (e) => {
      if (draggedEdge) {
        e.preventDefault();
        setDraggedEdge(null);
        onRangeChange({ start: startDate, end: endDate });
      }
    },
    [draggedEdge, startDate, endDate]
  );

  const handleDocumentMouseMove = useCallback(
    (event) => {
      if (!draggedEdge || !calendarElement) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const deltaX = event.clientX - lastEdgePosition;
      const { clientWidth } = calendarElement;

      if (draggedEdge === Direction.Left) {
        thumbLeft.current = calculateThumbLeft(deltaX, prevThumbWidth.current, prevThumbLeft.current);
      }
      thumbWidth.current = calculateThumbWidth(
        draggedEdge,
        deltaX,
        prevThumbWidth.current,
        prevThumbLeft.current,
        thumbLeft.current,
        clientWidth
      );
      const newEdgeDate = calculateEdgeDate(
        draggedEdge,
        deltaX,
        rangeDaysWidth.current,
        range.start,
        range.end,
        startDate,
        endDate,
        calendarStartDate,
        calendarEndDate
      );
      if (!newEdgeDate) {
        return;
      }
      if (draggedEdge === Direction.Left) {
        setStartDate(formatDatabaseDateTime(newEdgeDate));
      } else {
        setEndDate(formatDatabaseDateTime(newEdgeDate));
      }
    },
    [draggedEdge, lastEdgePosition, range]
  );

  useEffect(() => {
    setStartDate(range.start);
    setEndDate(range.end);
  }, [range]);

  useEffect(() => {
    document.addEventListener("mousemove", handleDocumentMouseMove);
    document.addEventListener("mouseup", handleDocumentMouseUp);
    document.addEventListener("mouseleave", handleDocumentMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleDocumentMouseMove);
      document.removeEventListener("mouseup", handleDocumentMouseUp);
      document.removeEventListener("mouseleave", handleDocumentMouseUp);
    };
  }, [handleDocumentMouseMove, handleDocumentMouseUp]);

  useEffect(() => {
    if (calendarElement) {
      const { offsetWidth, scrollWidth } = calendarElement;
      thumbWidth.current = Math.max((offsetWidth / scrollWidth) * offsetWidth, MIN_THUMB_WIDTH);
    }
  }, [range]);

  return { isDragging: !!draggedEdge, handleThumbEdgeMouseDown, startDate, endDate };
};

export default useChangeRange;
